import { TextField, TextFieldProps } from "@mui/material"
import { ReactElement, useEffect, useRef, useState } from "react"

import { DEBOUNCE_MS } from "../../constants/generalConstants"

/**
 * Debounced TextField component that delays triggering onChange until user stops typing.
 * Provides immediate UI feedback while debouncing the actual filter update.
 */
export interface DebouncedTextFieldProps extends Omit<
    TextFieldProps,
    "onChange"
> {
    value: string
    onDebouncedChange: (value: string) => void
    debounceMs?: number
    /** Optional transform function to apply to input immediately (e.g., toUpperCase) */
    transform?: (value: string) => string
}

const DebouncedTextField = ({
    value: externalValue,
    onDebouncedChange,
    debounceMs = DEBOUNCE_MS,
    transform,
    ...textFieldProps
}: DebouncedTextFieldProps): ReactElement => {
    const [localValue, setLocalValue] = useState(externalValue)

    // Store the latest callback in a ref to avoid resetting the debounce timer
    // when the parent component rerenders with a new callback instance
    const onDebouncedChangeRef = useRef(onDebouncedChange)

    // Update the ref whenever the callback changes
    useEffect(() => {
        onDebouncedChangeRef.current = onDebouncedChange
    }, [onDebouncedChange])

    // Sync local value when external value changes (e.g., from URL or reset)
    useEffect(() => {
        setLocalValue(externalValue)
    }, [externalValue])

    // Debounce the filter update
    useEffect(() => {
        const timeout = setTimeout(() => {
            if (localValue !== externalValue) {
                onDebouncedChangeRef.current(localValue)
            }
        }, debounceMs)

        return () => clearTimeout(timeout)
    }, [localValue, debounceMs, externalValue])

    return (
        <TextField
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...textFieldProps}
            value={localValue}
            onChange={(event) => {
                const newValue = transform
                    ? transform(event.target.value)
                    : event.target.value
                setLocalValue(newValue)
            }}
        />
    )
}

export default DebouncedTextField
